import { Head, Link } from "@inertiajs/react"
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Mail,
  Paperclip,
} from "lucide-react"
import { useState } from "react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface ArchivedEmail {
  key: string
  size: number
  uploaded: string
  from?: string
  to?: string
}

interface PreviewAttachment {
  filename: string
  content_type?: string
  size: number
}

interface Preview {
  subject?: string
  from?: string
  to?: string
  cc?: string
  date?: string
  message_id?: string
  text_body?: string
  html_body?: string
  attachments?: PreviewAttachment[]
  error?: string
}

interface Props {
  date: string
  today: string
  objects: ArchivedEmail[]
  cursor?: string
  truncated: boolean
  error?: string
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Admin Console", href: "/admin/console" },
  { title: "Email Archive", href: "/admin/email_archive" },
]

function shiftDate(iso: string, days: number) {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function fmtTime(iso: string) {
  return new Date(iso).toISOString().slice(11, 19)
}

function fmtSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${bytes} B`
}

export default function EmailArchive({
  date,
  today,
  objects,
  cursor,
  truncated,
  error,
}: Props) {
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)

  const openPreview = async (key: string) => {
    setPreviewKey(key)
    setPreview(null)
    try {
      const res = await fetch(
        `/admin/email_archive/preview?key=${encodeURIComponent(key)}`,
        { headers: { Accept: "application/json" } },
      )
      setPreview((await res.json()) as Preview)
    } catch {
      setPreview({ error: "Preview request failed" })
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Email Archive" />
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold">
              <Mail className="size-6" /> Email Archive
            </h1>
            <p className="text-muted-foreground text-sm">
              Every email received by *.rebulk.com subdomains, captured at the
              edge before any processing. 180-day retention, dates are UTC.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="icon" variant="outline">
              <Link
                aria-label="Previous day"
                href={`/admin/email_archive?date=${shiftDate(date, -1)}`}
              >
                <ChevronLeft className="size-4" />
              </Link>
            </Button>
            <span className="min-w-28 text-center font-medium tabular-nums">
              {date}
            </span>
            <Button
              asChild
              disabled={date >= today}
              size="icon"
              variant="outline"
            >
              <Link
                aria-label="Next day"
                href={`/admin/email_archive?date=${shiftDate(date, 1)}`}
              >
                <ChevronRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : objects.length === 0 ? (
          <Card>
            <CardContent className="text-muted-foreground py-10 text-center text-sm">
              No emails archived on {date}.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Received</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead className="w-20 text-right">Size</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {objects.map((o) => (
                    <TableRow
                      key={o.key}
                      className="cursor-pointer"
                      onClick={() => void openPreview(o.key)}
                    >
                      <TableCell className="text-muted-foreground tabular-nums">
                        {fmtTime(o.uploaded)}
                      </TableCell>
                      <TableCell className="max-w-64 truncate">
                        {o.from || "—"}
                      </TableCell>
                      <TableCell className="max-w-64 truncate">
                        {o.to || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-right tabular-nums">
                        {fmtSize(o.size)}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            aria-label="Preview"
                            size="icon"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              void openPreview(o.key)
                            }}
                          >
                            <Eye className="size-4" />
                          </Button>
                          <Button
                            asChild
                            aria-label="Download .eml"
                            size="icon"
                            variant="ghost"
                          >
                            <a
                              href={`/admin/email_archive/download?key=${encodeURIComponent(o.key)}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download className="size-4" />
                            </a>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {truncated && cursor && (
          <Button asChild variant="outline">
            <Link
              href={`/admin/email_archive?date=${date}&cursor=${encodeURIComponent(cursor)}`}
            >
              More…
            </Link>
          </Button>
        )}
      </div>

      <Dialog
        open={previewKey !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewKey(null)
        }}
      >
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
          {!preview ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : preview.error ? (
            <>
              <DialogHeader>
                <DialogTitle>Preview unavailable</DialogTitle>
              </DialogHeader>
              <Alert variant="destructive">
                <AlertDescription>{preview.error}</AlertDescription>
              </Alert>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8">
                  {preview.subject || "(no subject)"}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-0.5 text-left text-xs">
                    <div>From: {preview.from || "—"}</div>
                    <div>To: {preview.to || "—"}</div>
                    {preview.cc && <div>Cc: {preview.cc}</div>}
                    {preview.date && <div>Date: {preview.date}</div>}
                  </div>
                </DialogDescription>
              </DialogHeader>
              {(preview.attachments?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {preview.attachments?.map((a) => (
                    <Badge key={a.filename + a.size} variant="secondary">
                      <Paperclip className="mr-1 size-3" />
                      {a.filename} · {fmtSize(a.size)}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="min-h-0 flex-1 overflow-auto rounded border">
                {preview.html_body ? (
                  <iframe
                    className="h-[50vh] w-full bg-white"
                    sandbox=""
                    srcDoc={preview.html_body}
                    title="Email body"
                  />
                ) : (
                  <pre className="p-3 text-sm whitespace-pre-wrap">
                    {preview.text_body || "(empty body)"}
                  </pre>
                )}
              </div>
              {previewKey && (
                <div>
                  <Button asChild size="sm" variant="outline">
                    <a
                      href={`/admin/email_archive/download?key=${encodeURIComponent(previewKey)}`}
                    >
                      <Download className="mr-1 size-4" /> Download .eml
                    </a>
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
